// ✅ src/pages/VehicleV2/DayWiseLubeConsumptionReport.jsx
// FULL UPDATED FILE
//
// ✅ common localStorage / sessionStorage pattern
// ✅ useMemo + useCallback + useRef pattern
// ✅ pageLoading + dropdownLoading + listLoading
// ✅ prevents repeated initial API calls
// ✅ safer search / view all / clear / print / export flow
// ✅ keeps highlight-items support
// ✅ keeps Lube Outward navigation
// ✅ safer HO / BRANCH branch handling
//
// Backend mount:
//   app.use("/api/daywiselubeConsumptionReport", daywiseLubeConsumptionReport);
//
// Recommended endpoints:
//   GET /meta
//   GET /list
//   GET /highlight-items (optional)
//   GET /print (optional)

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaFileExport,
  FaInfoCircle,
  FaEye,
  FaSyncAlt,
} from "react-icons/fa";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/daywiselubeConsumptionReport";

/* ---------------- helpers ---------------- */
const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toNum = (v, d = 0) => {
  if (v === null || v === undefined || v === "") return d;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : d;
};

const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select";
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const monthNameToNumber = (m) => {
  const s = toStr(m).toLowerCase();
  const map = {
    january: 1,
    february: 2,
    march: 3,
    april: 4,
    may: 5,
    june: 6,
    july: 7,
    august: 8,
    september: 9,
    october: 10,
    november: 11,
    december: 12,
  };
  return map[s] || 0;
};

const daysInMonth = (year, monthName) => {
  const y = Number(year);
  const m = monthNameToNumber(monthName);
  if (!Number.isFinite(y) || !m) return 0;
  return new Date(y, m, 0).getDate();
};

const fmtNum = (v, digits = 2) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const exportToCsv = (rows, columns, filename = "day-wise-lube-consumption.csv") => {
  if (!rows?.length) return;

  const headers = columns?.length ? columns : Object.keys(rows[0] || {});
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const val = r?.[h] ?? "";
          const s = String(val).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.setAttribute("download", filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const createDefaultFilters = (branchType, branchId) => ({
  branchId: branchType === "HO" ? "Select" : String(branchId),
  month: "Select",
  year: "Select",
  regNo: "",
  vehicleName: "",
  vehicleCode: "",
  vehicleMainTypeId: "Select",
  itemCategoryId: "",
  itemId: "Select",
  itemNameText: "",
});

export default function DayWiseLubeConsumptionReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";

  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";

  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "BRANCH";

  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    localStorage.getItem("loginUserId") ||
    "0";

  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toNum(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "BRANCH"),
      loginUserId: toNum(loginUserId, 0),
      userId: toStr(userId || "0"),
    }),
    [sessionBranchId, branchType, loginUserId, userId]
  );

  const defaultFilters = useMemo(
    () => createDefaultFilters(session.branchType, session.branchId),
    [session.branchType, session.branchId]
  );

  /* ===========================
     refs
  =========================== */
  const didInitRef = useRef(false);

  /* ===========================
     loading states
  =========================== */
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  const isBusy = pageLoading || dropdownLoading || listLoading;

  /* ===========================
     UI states
  =========================== */
  const [showFilters, setShowFilters] = useState(true);
  const [vehicleType, setVehicleType] = useState("O"); // O=Own, H=Hire

  /* ===========================
     meta states
  =========================== */
  const [branches, setBranches] = useState([]);
  const [vehicleMainTypes, setVehicleMainTypes] = useState([]);
  const [itemCategory, setItemCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [highlightItems, setHighlightItems] = useState([]);

  /* ===========================
     form states
  =========================== */
  const [filters, setFilters] = useState(defaultFilters);

  /* ===========================
     result states
  =========================== */
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);

  /* ===========================
     pagination
  =========================== */
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  /* ===========================
     months / years
  =========================== */
  const months = useMemo(
    () => [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    []
  );

  const years = useMemo(() => {
    const y = new Date().getFullYear();
    const arr = [];
    for (let i = 2006; i <= y; i++) arr.push(String(i));
    return arr;
  }, []);

  const totalDays = useMemo(
    () => daysInMonth(filters.year, filters.month),
    [filters.year, filters.month]
  );

  /* ===========================
     styles
  =========================== */
  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : "bg-white text-black"
      }`,
    [isDark]
  );

  /* ===========================
     derived helpers
  =========================== */
  const displayColumns = useMemo(() => {
    if (columns?.length) return columns;
    if (rows?.[0]) return Object.keys(rows[0]);
    return [];
  }, [columns, rows]);

  const totalPages = Math.ceil(rows.length / itemsPerPage);

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return rows.slice(start, start + itemsPerPage);
  }, [rows, currentPage]);

  /* ===========================
     loaders
  =========================== */
  const loadHighlightItems = useCallback(
    async (nextFilters = filters, nextVehicleType = vehicleType) => {
      try {
        const month = toStr(nextFilters.month);
        const year = toStr(nextFilters.year);
        const branchIdQ =
          session.branchType === "HO"
            ? toStr(nextFilters.branchId)
            : String(session.branchId);

        if (isSelect(month) || isSelect(year)) {
          setHighlightItems([]);
          return;
        }

        const { data } = await axios.get(`${API_BASE}/highlight-items`, {
          params: {
            month,
            year,
            branchId: branchIdQ,
            vehType: nextVehicleType,
          },
          headers: commonHeaders,
        });

        if (!data?.success) {
          setHighlightItems([]);
          return;
        }

        setHighlightItems(Array.isArray(data.items) ? data.items : []);
      } catch {
        setHighlightItems([]);
      }
    },
    [filters, vehicleType, session.branchType, session.branchId, commonHeaders]
  );

  const loadMeta = useCallback(async () => {
    try {
      setDropdownLoading(true);

      const { data } = await axios.get(`${API_BASE}/meta`, {
        params: {
          branchType: session.branchType,
          branchId: session.branchId,
          loginUserId: session.loginUserId,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Meta failed");
      }

      const br = toList(data.branches);
      const vmt = toList(data.vehicleMainTypes);
      const cat = data.itemCategory || null;
      const it = toList(data.items);

      setBranches(br);
      setVehicleMainTypes(vmt);
      setItemCategory(cat);
      setItems(it);

      setFilters((prev) => ({
        ...prev,
        branchId:
          session.branchType === "HO"
            ? prev.branchId
            : String(session.branchId),
        itemCategoryId: String(
          cat?.fld_itemcategoryid ?? prev.itemCategoryId ?? ""
        ),
      }));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to load meta");
      setBranches([]);
      setVehicleMainTypes([]);
      setItemCategory(null);
      setItems([]);
      setHighlightItems([]);
    } finally {
      setDropdownLoading(false);
    }
  }, [session.branchType, session.branchId, session.loginUserId, commonHeaders]);

  const buildListParams = useCallback(
    (f = filters, nextVehicleType = vehicleType) => ({
      vehType: nextVehicleType,
      branchType: session.branchType,
      sessionBranchId: session.branchId,
      loginUserId: session.loginUserId,
      branchId:
        session.branchType === "HO"
          ? (isSelect(f.branchId) ? "Select" : toStr(f.branchId))
          : String(session.branchId),
      month: isSelect(f.month) ? "Select" : toStr(f.month),
      year: isSelect(f.year) ? "Select" : toStr(f.year),
      regNo: toStr(f.regNo),
      vehicleCode: toStr(f.vehicleCode),
      vehicleName: toStr(f.vehicleName),
      vehicleMainTypeId: isSelect(f.vehicleMainTypeId)
        ? "Select"
        : toStr(f.vehicleMainTypeId),
      itemCategoryId: toStr(f.itemCategoryId),
      itemId: isSelect(f.itemId) ? "Select" : toStr(f.itemId),
      itemName: toStr(f.itemNameText),
    }),
    [filters, vehicleType, session.branchType, session.branchId, session.loginUserId]
  );

  const loadList = useCallback(
    async (nextFilters = filters, nextVehicleType = vehicleType, silent = false) => {
      try {
        setListLoading(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: buildListParams(nextFilters, nextVehicleType),
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Search failed");
        }

        const list = toList(data.rows);
        const cols = toList(data.columns)?.length
          ? data.columns
          : list[0]
          ? Object.keys(list[0])
          : [];

        setRows(list);
        setColumns(cols);
        setCurrentPage(1);

        if (!silent) {
          toast.success(`Found ${list.length} records`);
        }
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.message || err?.message || "Search failed");
        setRows([]);
        setColumns([]);
        setCurrentPage(1);
      } finally {
        setListLoading(false);
      }
    },
    [filters, vehicleType, buildListParams, commonHeaders]
  );

  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);

      const baseFilters = createDefaultFilters(session.branchType, session.branchId);
      setFilters(baseFilters);
      setRows([]);
      setColumns([]);
      setHighlightItems([]);
      setCurrentPage(1);

      await loadMeta();
    } finally {
      setPageLoading(false);
    }
  }, [session.branchType, session.branchId, loadMeta]);

  /* ===========================
     handlers
  =========================== */
  const setField = useCallback((name, value) => {
    setFilters((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "itemId") {
        const selected = items.find(
          (x) => String(x.fld_itemid) === String(value)
        );
        next.itemNameText = selected?.fld_itemname || "";
      }

      return next;
    });
  }, [items]);

  const handleChange = useCallback(
    async (e) => {
      const { name, value } = e.target;

      let nextFilters = null;

      setFilters((prev) => {
        const next = { ...prev, [name]: value };

        if (name === "itemId") {
          const selected = items.find(
            (x) => String(x.fld_itemid) === String(value)
          );
          next.itemNameText = selected?.fld_itemname || "";
        }

        nextFilters = next;
        return next;
      });

      if (
        name === "month" ||
        name === "year" ||
        name === "branchId"
      ) {
        const safeNext =
          nextFilters ||
          { ...filters, [name]: value };

        await loadHighlightItems(safeNext, vehicleType);
      }
    },
    [items, filters, vehicleType, loadHighlightItems]
  );

  const validate = useCallback(
    (f = filters) => {
      if (isSelect(f.month)) return "Please Select the Month";
      if (isSelect(f.year)) return "Please Select the Year";
      if (isSelect(f.itemId)) return "Please Select the Item Name";
      return "";
    },
    [filters]
  );

  const handleSearch = useCallback(async () => {
    const msg = validate(filters);
    if (msg) {
      toast.warning(msg);
      return;
    }
    await loadList(filters, vehicleType);
  }, [filters, vehicleType, validate, loadList]);

  const handleViewAll = useCallback(async () => {
    const reset = {
      ...filters,
      regNo: "",
      vehicleName: "",
      vehicleCode: "",
      vehicleMainTypeId: "Select",
    };

    const msg = validate(reset);
    if (msg) {
      toast.warning(msg);
      return;
    }

    setFilters(reset);
    await loadList(reset, vehicleType);
    toast.info("Showing all vehicles for selected month/year/item");
  }, [filters, vehicleType, validate, loadList]);

  const handleClearFilters = useCallback(() => {
    const cleared = {
      ...createDefaultFilters(session.branchType, session.branchId),
      itemCategoryId: toStr(itemCategory?.fld_itemcategoryid || ""),
    };

    setFilters(cleared);
    setRows([]);
    setColumns([]);
    setHighlightItems([]);
    setCurrentPage(1);
    toast.info("Cleared filters");
  }, [session.branchType, session.branchId, itemCategory]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
    toast.info("Page refreshed");
  }, [loadPage]);

  const handleExport = useCallback(() => {
    if (!rows.length) {
      toast.warning("No data to export");
      return;
    }
    exportToCsv(rows, displayColumns, "day-wise-lube-consumption.csv");
    toast.success("Exported CSV");
  }, [rows, displayColumns]);

  const handlePrint = useCallback(() => {
    const msg = validate(filters);
    if (msg) {
      toast.warning(msg);
      return;
    }

    const qs = new URLSearchParams({
      vehType: vehicleType,
      branchId:
        session.branchType === "HO"
          ? (isSelect(filters.branchId) ? "Select" : toStr(filters.branchId))
          : String(session.branchId),
      month: toStr(filters.month),
      year: toStr(filters.year),
      regNo: toStr(filters.regNo),
      vehicleCode: toStr(filters.vehicleCode),
      vehicleName: toStr(filters.vehicleName),
      vehicleMainTypeId: isSelect(filters.vehicleMainTypeId)
        ? "Select"
        : toStr(filters.vehicleMainTypeId),
      itemCategoryId: toStr(filters.itemCategoryId),
      itemId: isSelect(filters.itemId) ? "Select" : toStr(filters.itemId),
      itemName: toStr(filters.itemNameText),
    }).toString();

    window.open(`/vehicle/DayWIseLubeConsumptionReport_Print?${qs}`, "_blank");
  }, [filters, vehicleType, validate, session.branchType, session.branchId]);

  const handleVehicleTypeChange = useCallback(
    async (nextType) => {
      setVehicleType(nextType);
      await loadHighlightItems(filters, nextType);
    },
    [filters, loadHighlightItems]
  );

  const handleLubeOutward = useCallback(() => {
    navigate("/vehicle/DayWiseLubeOutwardReport", {
      state: {
        vehType: vehicleType,
        branchId:
          session.branchType === "HO"
            ? filters.branchId
            : String(session.branchId),
        month: filters.month,
        year: filters.year,
        regNo: filters.regNo,
        vehicleCode: filters.vehicleCode,
        vehicleName: filters.vehicleName,
        vehicleMainTypeId: filters.vehicleMainTypeId,
        itemCategoryId: filters.itemCategoryId,
        itemId: filters.itemId,
        itemName: filters.itemNameText,
      },
    });
  }, [navigate, vehicleType, filters, session.branchType, session.branchId]);

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    loadPage();
  }, [loadPage]);

  /* ===========================
     pagination handler
  =========================== */
  const paginate = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              DAY WISE LUBE CONSUMPTION REPORT
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Top header */}
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Day Wise Lube Consumption Report
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {rows.length} Records
              </span>

              {!!totalDays && !isSelect(filters.month) && !isSelect(filters.year) && (
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDark ? "bg-gray-800 text-gray-200" : "bg-gray-100 text-gray-800"
                  }`}
                >
                  Days: {totalDays}
                </span>
              )}

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isBusy
                    ? isDark
                      ? "bg-yellow-900 text-yellow-100"
                      : "bg-yellow-100 text-yellow-800"
                    : isDark
                    ? "bg-green-900 text-green-100"
                    : "bg-green-100 text-green-800"
                }`}
              >
                {pageLoading
                  ? "Loading Page"
                  : dropdownLoading
                  ? "Loading Dropdowns"
                  : listLoading
                  ? "Loading List"
                  : "Ready"}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters((s) => !s)}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? `border-gray-600 text-white hover:bg-gray-800 ${
                        showFilters ? "bg-gray-800" : ""
                      }`
                    : `border-gray-300 text-gray-700 hover:bg-gray-50 ${
                        showFilters ? "bg-gray-50" : ""
                      }`
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleExport}
                disabled={isBusy || rows.length === 0}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy || rows.length === 0 ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFileExport className="w-4 h-4" />
                Export
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={() => navigate(-1)}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {pageLoading || dropdownLoading ? (
            <div className="text-center py-16">
              <div
                className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                  isDark ? "bg-gray-700" : "bg-gray-100"
                }`}
              >
                <FaInfoCircle className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                Loading...
              </h3>
              <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Please wait while data is loading.
              </p>
            </div>
          ) : (
            <>
              {/* Own/Hire */}
              <div className="flex gap-6 mb-6">
                <label className={`flex items-center gap-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  <input
                    type="radio"
                    checked={vehicleType === "O"}
                    onChange={() => handleVehicleTypeChange("O")}
                    disabled={isBusy}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  Own Vehicle
                </label>

                <label className={`flex items-center gap-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  <input
                    type="radio"
                    checked={vehicleType === "H"}
                    onChange={() => handleVehicleTypeChange("H")}
                    disabled={isBusy}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  Hire Vehicle
                </label>
              </div>

              {/* Filters */}
              {showFilters && (
                <div
                  className={`rounded-xl p-6 mb-6 border ${
                    isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                    <h3
                      className={`text-lg font-semibold flex items-center gap-2 ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      <FaFilter /> FILTRATION AREA
                    </h3>

                    <button
                      type="button"
                      onClick={handleLubeOutward}
                      disabled={isBusy}
                      className={`px-6 py-2 rounded-lg font-bold shadow-lg border transition-all ${
                        isDark
                          ? "bg-purple-700 hover:bg-purple-800 text-white border-purple-400"
                          : "bg-purple-600 hover:bg-purple-700 text-white border-purple-200"
                      } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      Lube Outward
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <div>
                      <label className={labelClass}>Branch</label>
                      <select
                        name="branchId"
                        value={filters.branchId}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy || session.branchType !== "HO"}
                      >
                        <option value="Select">Select</option>
                        {branches.map((b, idx) => (
                          <option
                            key={idx}
                            value={String(
                              b.fld_branchid ?? b.branchid ?? b.id ?? b.value ?? ""
                            )}
                          >
                            {b.fld_BranchName ?? b.branchname ?? b.name ?? b.label ?? ""}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Month</label>
                      <select
                        name="month"
                        value={filters.month}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value="Select">Select</option>
                        {months.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Year</label>
                      <select
                        name="year"
                        value={filters.year}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value="Select">Select</option>
                        {years.map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Registration Number</label>
                      <input
                        type="text"
                        name="regNo"
                        value={filters.regNo}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Enter registration number"
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Name</label>
                      <input
                        type="text"
                        name="vehicleName"
                        value={filters.vehicleName}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Enter vehicle name"
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Code</label>
                      <input
                        type="text"
                        name="vehicleCode"
                        value={filters.vehicleCode}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Enter vehicle code"
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Main Type</label>
                      <select
                        name="vehicleMainTypeId"
                        value={filters.vehicleMainTypeId}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value="Select">Select</option>
                        {vehicleMainTypes.map((v, idx) => (
                          <option
                            key={idx}
                            value={String(
                              v.fld_vehicletypeid ??
                                v.id ??
                                v.value ??
                                v.fld_vehiclemaintypeid ??
                                "Select"
                            )}
                          >
                            {v.fld_vehicletypename ??
                              v.name ??
                              v.label ??
                              v.fld_vehicletypeindex ??
                              ""}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Item Category</label>
                      <input
                        className={inputClass}
                        value={itemCategory?.fld_itemcategory_name || "FUELS AND LUBRICANTS"}
                        readOnly
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Item Name</label>
                      <select
                        name="itemId"
                        value={filters.itemId}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value="Select">Select</option>
                        {items.map((it) => {
                          const name = it.fld_itemname;
                          const isHi = highlightItems.includes(name);
                          return (
                            <option
                              key={it.fld_itemid}
                              value={String(it.fld_itemid)}
                              style={isHi ? { backgroundColor: "#00e6e6" } : undefined}
                            >
                              {name}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={handleClearFilters}
                      type="button"
                      disabled={isBusy}
                      className={`text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200 ${
                        isBusy ? "opacity-60 cursor-not-allowed" : ""
                      }`}
                    >
                      Clear All Filters
                    </button>

                    <div className="flex flex-wrap gap-4">
                      <button
                        onClick={handleViewAll}
                        disabled={isBusy}
                        type="button"
                        className={`flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        <FaEye />
                        View All
                      </button>

                      <button
                        onClick={handleSearch}
                        disabled={isBusy}
                        type="button"
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        <FaSearch />
                        {listLoading ? "Loading..." : "Search Records"}
                      </button>

                      <button
                        onClick={handlePrint}
                        disabled={isBusy}
                        type="button"
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        <FaPrint />
                        Print
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Results */}
              <div
                className={`rounded-xl shadow-sm border overflow-hidden ${
                  isDark ? "border-gray-700" : "border-gray-200"
                }`}
              >
                {rows.length === 0 ? (
                  <div className="text-center py-16">
                    <div
                      className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                        isDark ? "bg-gray-700" : "bg-gray-100"
                      }`}
                    >
                      <FaInfoCircle className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                      No Records
                    </h3>
                    <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      Select Month, Year and Item Name, then click Search.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className={`${buttonClass} text-white`}>
                        <tr>
                          {displayColumns.map((c) => (
                            <th
                              key={c}
                              className="px-4 py-3 text-center font-semibold whitespace-nowrap"
                            >
                              {c}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {pagedRows.map((r, idx) => (
                          <tr
                            key={idx}
                            className={`${
                              isDark ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-gray-50"
                            } transition-colors duration-200`}
                          >
                            {displayColumns.map((c) => {
                              const v = r?.[c];
                              const isNum =
                                typeof v === "number" ||
                                (typeof v === "string" &&
                                  v !== "" &&
                                  !Number.isNaN(Number(v)));
                              return (
                                <td
                                  key={c}
                                  className={`px-4 py-3 text-sm text-center whitespace-nowrap ${
                                    isDark ? "text-gray-300" : "text-gray-900"
                                  }`}
                                >
                                  {isNum ? fmtNum(v, 2) : toStr(v)}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {rows.length > 0 && (
                <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
                  <div className={`text-sm mb-4 sm:mb-0 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                    {Math.min(currentPage * itemsPerPage, rows.length)} of {rows.length} entries
                  </div>

                  <nav className="flex gap-1">
                    <button
                      onClick={() => paginate(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === 1
                          ? isDark
                            ? "border-gray-700 text-gray-500 cursor-not-allowed"
                            : "border-gray-300 text-gray-400 cursor-not-allowed"
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      Previous
                    </button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => paginate(pageNum)}
                          className={`px-3 py-1 border rounded text-sm ${
                            currentPage === pageNum
                              ? `${buttonClass} text-white`
                              : isDark
                              ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                              : "border-gray-300 text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === totalPages
                          ? isDark
                            ? "border-gray-700 text-gray-500 cursor-not-allowed"
                            : "border-gray-300 text-gray-400 cursor-not-allowed"
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      Next
                    </button>
                  </nav>
                </div>
              )}

              {isBusy && (
                <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  {pageLoading
                    ? "Loading page..."
                    : dropdownLoading
                    ? "Loading dropdowns..."
                    : listLoading
                    ? "Loading list..."
                    : "Loading..."}
                </div>
              )}
            </>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}